import { useState, useRef, useEffect, useCallback } from "react";
import { elakay_API } from "../../config";
import { elakayAutoText } from "./dataElakay";
import DraggableFloatingPanel from "./DraggableFloatingPanel";
import { isDesignedQrSelection, resolvePosterQrDesign } from "./posterQrDesign";


let __elakayInfoSource;
const __setElakayInfoSource = (infoSource) => { if (infoSource) __elakayInfoSource = infoSource; };
const __elakayText = (key, fallback) =>
  __elakayInfoSource?.laguangeElakay?.("frontElakayHardcodedText", __elakayInfoSource.languageAPI)?.["imageCombiner"]?.[key] || fallback;

let imageCombinerInfoSource;
const imageCombinerText = (key, fallback) =>
  imageCombinerInfoSource?.laguangeElakay?.("frontElakayHardcodedText", imageCombinerInfoSource.languageAPI)?.imageCombiner?.[key] || fallback;

/* ─── Default posters ──────────────────────────────────────────────────────── */
// const DEFAULT_POSTERS = [
  // { id: "p1", label: "Americas",  url: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=80" },
  // { id: "p2", label: "Business",  url: "https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=800&q=80" },
  // { id: "p3", label: "Network",   url: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&q=80" },
  // { id: "p4", label: "Market",    url: "https://images.unsplash.com/photo-1542744094-24638eff58bb?w=800&q=80" },
  // { id: "p5", label: "Gold",      url: "https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=800&q=80" },
  // { id: "p6", label: "Trade",     url: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&q=80" },
// ];

/* ─── Default QR codes ─────────────────────────────────────────────────────── */
// const DEFAULT_QRS = [
//   { id: "q1", label: "Elakay.com",  url: "https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=https://elakay.com&color=000000&bgcolor=ffffff" },
//   { id: "q2", label: "App Store",   url: "https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=https://apps.apple.com&color=000000&bgcolor=ffffff" },
//   { id: "q3", label: "Google Play", url: "https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=https://play.google.com&color=000000&bgcolor=ffffff" },
//   { id: "q4", label: "Join",        url: "https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=https://elakay.com/join&color=000000&bgcolor=ffffff" },
//   { id: "q5", label: "Contact",     url: "https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=https://elakay.com/contact&color=000000&bgcolor=ffffff" },
// ];

const QR_PRESETS = [
  { label: "XS", pct: 10 },
  { label: "S",  pct: 18 },
  { label: "M",  pct: 26 },
  { label: "L",  pct: 34 },
  { label: "XL", pct: 45 },
];

const QUICK_POS = [
  { label: "↖", x: 12, y: 12 }, { label: "↑", x: 50, y: 12 }, { label: "↗", x: 88, y: 12 },
  { label: "←", x: 12, y: 50 }, { label: "·", x: 50, y: 50 }, { label: "→", x: 88, y: 50 },
  { label: "↙", x: 12, y: 88 }, { label: "↓", x: 50, y: 88 }, { label: "↘", x: 88, y: 88 },
];

/* ─── Theme colors (matching the existing page CSS) ────────────────────────── */
const T = {
  blue:       "#0070cc",
  blueBg:     "#e8f3ff",
  blueFocus:  "rgba(0,112,204,0.1)",
  green:      "#1D9E75",
  greenBg:    "#e1f5ee",
  warn:       "#c45000",
  warnBg:     "#fff3e0",
  red:        "#dc3545",
  redBg:      "#fff0f1",
  bg:         "#f8f9fa",
  white:      "#ffffff",
  card:       "#ffffff",
  border:     "rgba(0,0,0,0.1)",
  borderMid:  "rgba(0,0,0,0.15)",
  text:       "#1a1a1a",
  muted:      "#6c757d",
  faint:      "#aaa",
  light:      "#f5f5f5",
  headerBg:   "#fafafa",
};

/* ─── Image loaders ────────────────────────────────────────────────────────── */
function loadImgFromUrl(url) {
  return new Promise((res, rej) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = url;
  });
}
function loadImgFromFile(file) {
  return new Promise((res, rej) => {
    const img = new Image();
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = URL.createObjectURL(file);
  });
}

/* ─── Thumbnail ────────────────────────────────────────────────────────────── */
function Thumb({ url, label, selected, onClick, size = 82 }) {
  return (
    <div onClick={onClick} title={label} style={{
      width: size, height: size, flexShrink: 0, cursor: "pointer", position: "relative",
      borderRadius: 8, overflow: "hidden",
      border: selected ? `2.5px solid ${T.blue}` : `1.5px solid ${T.border}`,
      boxShadow: selected ? `0 0 0 3px ${T.blueBg}` : "0 1px 4px rgba(0,0,0,0.08)",
      transition: "all .15s",
    }}>
      <img src={url} alt={label}
        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
      {selected && (
        <div style={{
          position: "absolute", top: 4, right: 4, width: 16, height: 16,
          background: T.blue, borderRadius: "50%",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 9, color: "#fff", fontWeight: 900,
        }}>✓</div>
      )}
    </div>
  );
}

/* ─── Upload tile ──────────────────────────────────────────────────────────── */
function UploadTile({ label, onFile, inputRef, size = 82 }) {
  const [hover, setHover] = useState(false);
  return (
    <>
      <input ref={inputRef} type="file" accept="image/*" style={{ display: "none" }}
        onChange={e => onFile(e.target.files[0])} />
      <div
        onClick={() => inputRef.current.click()}
        onDrop={e => { e.preventDefault(); onFile(e.dataTransfer.files[0]); }}
        onDragOver={e => e.preventDefault()}
        onMouseOver={() => setHover(true)}
        onMouseOut={() => setHover(false)}
        style={{
          width: size, height: size, flexShrink: 0, cursor: "pointer",
          borderRadius: 8, border: `1.5px dashed ${hover ? T.blue : T.borderMid}`,
          background: hover ? T.blueBg : T.light,
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center", gap: 4,
          transition: "all .15s",
        }}
      >
        <span style={{ fontSize: 22, color: hover ? T.blue : T.faint }}>+</span>
        <span style={{ fontSize: 9, color: hover ? T.blue : T.muted, textAlign: "center", lineHeight: 1.3, padding: "0 4px" }}>
          {label}
        </span>
      </div>
    </>
  );
}

/* ─── Card shell matching .qr-card ────────────────────────────────────────── */
function Card({ children }) {
  return (
    <div style={{
      background: T.white, border: `0.5px solid ${T.border}`,
      borderRadius: 12, overflow: "hidden",
      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    }}>
      {children}
    </div>
  );
}

function CardHeader({ icon, iconType = "info", title, badge, children }) {
  const iconColors = {
    info:    { bg: "#e0f0ff", color: "#0070cc" },
    success: { bg: "#e1f5ee", color: "#085041" },
    warn:    { bg: "#fff3e0", color: "#c45000" },
    purple:  { bg: "#f3e8ff", color: "#6d28d9" },
  };
  const ic = iconColors[iconType] || iconColors.info;
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10, padding: "13px 16px",
      borderBottom: `0.5px solid rgba(0,0,0,0.08)`, background: T.headerBg,
    }}>
      <div style={{
        width: 30, height: 30, borderRadius: 8, flexShrink: 0,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: ic.bg, color: ic.color, fontSize: 15,
      }}>{icon}</div>
      <span style={{ flex: 1, fontSize: 13, fontWeight: 500, color: T.text }}>{title}</span>
      {badge !== undefined && (
        <span style={{
          fontSize: 10, padding: "2px 8px", borderRadius: 20,
          background: "#f0f0f0", color: "#666",
          border: `0.5px solid rgba(0,0,0,0.08)`,
        }}>{badge}</span>
      )}
      {children}
    </div>
  );
}

function CardBody({ children }) {
  return (
    <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 14 }}>
      {children}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════════════════════ */
export default function ImageCombiner({DEFAULT_QRS , DEFAULT_POSTERS,  selectedPromoId, url, promoInfo, infoSource, getPubToken, token, addUpdateGraphicPromo } ) {
  __setElakayInfoSource(infoSource);
  __setElakayInfoSource(infoSource);
  imageCombinerInfoSource = infoSource;
  /* ── floating panels state ── */
const [canvasFloating, setCanvasFloating]   = useState(false);
const [previewFloating, setPreviewFloating] = useState(false);

/* ── image scale (en %) ── */
const [canvasScale, setCanvasScale]   = useState(100);
const [previewScale, setPreviewScale] = useState(100);

  /* ── asset selection ── */
  const [selectedPosterId, setSelectedPosterId] = useState("p1");
  const [selectedQrId,     setSelectedQrId]     = useState(selectedPromoId);
  const [userPosters,      setUserPosters]       = useState([]);
  const [userQrs,          setUserQrs]           = useState([]);

  /* ── QR source mode: "gallery" | "url" ── */
  const [qrMode,    setQrMode]    = useState("gallery");
  const [qrUrlInput, setQrUrlInput] = useState(url || "");
  const [qrUrlError, setQrUrlError] = useState("");

  /* ── loaded image elements ── */
  const [posterImg, setPosterImg] = useState(null);
  const [qrImg,     setQrImg]     = useState(null);
  const [qrRawImg,  setQrRawImg]  = useState(null); // original uncolored QR

  /* ── QR color & logo customization ── */
  const [qrFgColor,    setQrFgColor]    = useState("#000000");
  const [qrBgColor,    setQrBgColor]    = useState("#ffffff");
  const [qrBgTransp,   setQrBgTransp]   = useState(false);
  const [logoImg,      setLogoImg]      = useState(null);
  const [logoPct,      setLogoPct]      = useState(22);
  const [logoRound,    setLogoRound]    = useState(8);
  const [logoBg,       setLogoBg]       = useState(true);
  const logoUpRef = useRef();

  /* ── QR gradient ── */
  const [gradEnabled,  setGradEnabled]  = useState(false);
  const [gradType,     setGradType]     = useState("radial");
  const [gradColor1,   setGradColor1]   = useState("#0070cc");
  const [gradColor2,   setGradColor2]   = useState("#c9a227");
  const [gradInvert,   setGradInvert]   = useState(false);

  /* ── QR border-radius ── */
  const [qrCornerRadius, setQrCornerRadius] = useState(0);  // 0–50% — rounds the whole QR frame
  const [qrDotStyle,     setQrDotStyle]     = useState("square"); // "square" | "dot" | "rounded"
  const [qrDotRadius,    setQrDotRadius]    = useState(30); // 0–50% per module (used when dotStyle≠square)

  // Helper: hex → [r,g,b]
  const hexToRgb = h => [parseInt(h.slice(1,3),16), parseInt(h.slice(3,5),16), parseInt(h.slice(5,7),16)];
  // Linear interp
  const lerp = (a, b, t) => Math.round(a + (b - a) * t);

  // Build a colored+logo+gradient+border-radius composited QR canvas
  const buildQrCanvas = useCallback((rawImg, fgColor, bgColor, bgTransp, logo, lPct, lRound, lBg,
    grad, gType, gC1, gC2, gInvert, cornerRadius, dotStyle, dotRadius) => {
    if (!rawImg) return null;
    const SIZE = 600;

    // ── Step 1: detect modules via an analysis canvas ──
    const analysisCvs = document.createElement("canvas");
    analysisCvs.width = analysisCvs.height = SIZE;
    const aC = analysisCvs.getContext("2d");
    aC.drawImage(rawImg, 0, 0, SIZE, SIZE);
    const raw = aC.getImageData(0, 0, SIZE, SIZE).data;

    // Estimate module grid by sampling brightness
    // QR codes have quiet zone + modules; estimate cell size ~SIZE/modules
    // We detect module size by scanning the first row for transitions
    const detectModuleSize = () => {
      let transitions = [], lastDark = null;
      for (let x = 10; x < SIZE - 10; x++) {
        const i = (Math.round(SIZE * 0.12) * SIZE + x) * 4;
        const lum = 0.299 * raw[i] + 0.587 * raw[i+1] + 0.114 * raw[i+2];
        const dark = lum < 128;
        if (lastDark !== null && dark !== lastDark) transitions.push(x);
        lastDark = dark;
      }
      if (transitions.length < 2) return SIZE / 25;
      const gaps = [];
      for (let i = 1; i < transitions.length; i++) gaps.push(transitions[i] - transitions[i-1]);
      gaps.sort((a,b) => a-b);
      return gaps[Math.floor(gaps.length / 2)] || SIZE / 25;
    };
    const modSize = detectModuleSize();

    // ── Step 2: build the output canvas ──
    const offscreen = document.createElement("canvas");
    offscreen.width = offscreen.height = SIZE;
    const ctx = offscreen.getContext("2d");

    // Fill background first
    if (!bgTransp) {
      ctx.fillStyle = bgColor;
      ctx.fillRect(0, 0, SIZE, SIZE);
    }

    // parse colors
    const [fgR, fgG, fgB] = hexToRgb(fgColor);
    const [bgR, bgG, bgB] = hexToRgb(bgColor);
    const [g1r, g1g, g1b] = hexToRgb(gInvert ? gC2 : gC1);
    const [g2r, g2g, g2b] = hexToRgb(gInvert ? gC1 : gC2);
    const cx2 = SIZE / 2, cy2 = SIZE / 2;
    const maxDist = Math.sqrt(cx2 * cx2 + cy2 * cy2);

    const getModuleColor = (mx, my) => {
      if (!grad) return `rgb(${fgR},${fgG},${fgB})`;
      let t = 0;
      if (gType === "radial") {
        const dx = mx - cx2, dy = my - cy2;
        t = Math.min(1, Math.sqrt(dx*dx + dy*dy) / maxDist);
      } else if (gType === "linear-h") { t = mx / SIZE; }
      else if (gType === "linear-v")   { t = my / SIZE; }
      else if (gType === "linear-d")   { t = (mx + my) / (SIZE * 2); }
      return `rgb(${lerp(g1r,g2r,t)},${lerp(g1g,g2g,t)},${lerp(g1b,g2b,t)})`;
    };

    if (dotStyle === "square") {
      // Pixel-perfect: re-color pixel by pixel (fast, preserves finder patterns)
      aC.drawImage(rawImg, 0, 0, SIZE, SIZE);
      const imgData = aC.getImageData(0, 0, SIZE, SIZE);
      const d = imgData.data;
      for (let y = 0; y < SIZE; y++) {
        for (let x = 0; x < SIZE; x++) {
          const i = (y * SIZE + x) * 4;
          const lum = 0.299 * d[i] + 0.587 * d[i+1] + 0.114 * d[i+2];
          if (lum < 128) {
            const col = getModuleColor(x, y);
            const [r,g,b] = col.match(/\d+/g).map(Number);
            d[i] = r; d[i+1] = g; d[i+2] = b; d[i+3] = 255;
          } else {
            d[i] = bgR; d[i+1] = bgG; d[i+2] = bgB;
            d[i+3] = bgTransp ? 0 : 255;
          }
        }
      }
      ctx.putImageData(imgData, 0, 0);
    } else {
      // Dot / Rounded: sample center of each cell
      const r = (dotRadius / 100) * modSize / 2;
      const isCircle = dotStyle === "dot";
      for (let row = 0; row < Math.ceil(SIZE / modSize); row++) {
        for (let col = 0; col < Math.ceil(SIZE / modSize); col++) {
          const cellX = col * modSize, cellY = row * modSize;
          const sampleX = Math.round(cellX + modSize / 2);
          const sampleY = Math.round(cellY + modSize / 2);
          if (sampleX >= SIZE || sampleY >= SIZE) continue;
          const si = (sampleY * SIZE + sampleX) * 4;
          const lum = 0.299 * raw[si] + 0.587 * raw[si+1] + 0.114 * raw[si+2];
          if (lum >= 128) continue; // background cell

          const pad = modSize * 0.07;
          const mx = cellX + pad, my = cellY + pad;
          const mw = modSize - pad * 2, mh = modSize - pad * 2;
          const cx3 = cellX + modSize / 2, cy3 = cellY + modSize / 2;

          ctx.fillStyle = getModuleColor(cx3, cy3);
          ctx.beginPath();
          if (isCircle) {
            ctx.arc(cx3, cy3, mw / 2, 0, Math.PI * 2);
          } else {
            // rounded rect
            const rr = Math.min(r, mw / 2, mh / 2);
            ctx.roundRect(mx, my, mw, mh, rr);
          }
          ctx.fill();
        }
      }
    }

    // ── Step 3: Outer corner radius clip ──
    if (cornerRadius > 0) {
      const cr = (cornerRadius / 100) * SIZE / 2;
      // Keep only inside the rounded rectangle
      const mask = document.createElement("canvas");
      mask.width = mask.height = SIZE;
      const mCtx = mask.getContext("2d");
      mCtx.fillStyle = "#000";
      mCtx.beginPath();
      mCtx.roundRect(0, 0, SIZE, SIZE, cr);
      mCtx.fill();
      // Apply: composite existing offscreen onto mask
      const result = document.createElement("canvas");
      result.width = result.height = SIZE;
      const rCtx = result.getContext("2d");
      rCtx.drawImage(mask, 0, 0);
      rCtx.globalCompositeOperation = "source-in";
      rCtx.drawImage(offscreen, 0, 0);
      // Copy result back to offscreen
      ctx.clearRect(0, 0, SIZE, SIZE);
      ctx.drawImage(result, 0, 0);
    }

    // ── Step 4: Overlay logo at center ──
    if (logo) {
      const lSize = Math.round(SIZE * (lPct / 100));
      const lx = (SIZE - lSize) / 2;
      const ly = (SIZE - lSize) / 2;
      const pad = lBg ? 8 : 0;
      if (lBg) {
        ctx.save();
        ctx.fillStyle = "#ffffff";
        ctx.beginPath();
        ctx.roundRect(lx - pad, ly - pad, lSize + pad*2, lSize + pad*2, lRound + pad);
        ctx.fill();
        ctx.restore();
      }
      ctx.save();
      ctx.beginPath();
      ctx.roundRect(lx, ly, lSize, lSize, lRound);
      ctx.clip();
      ctx.drawImage(logo, lx, ly, lSize, lSize);
      ctx.restore();
    }
    return offscreen;
  }, []);

  // A QR made in the QR Code Designer is inserted exactly as designed (its
  // photo, colors and checked readability): no recolor / logo / reshaping.
  const insertedDesign = isDesignedQrSelection(qrMode, selectedQrId, DEFAULT_QRS || []);

  // Recompose whenever any QR style param changes
  useEffect(() => {
    if (!qrRawImg) return;
    if (insertedDesign) {
      setQrImg(qrRawImg);
      setPreview(null);
      return;
    }
    const composed = buildQrCanvas(
      qrRawImg, qrFgColor, qrBgColor, qrBgTransp,
      logoImg, logoPct, logoRound, logoBg,
      gradEnabled, gradType, gradColor1, gradColor2, gradInvert,
      qrCornerRadius, qrDotStyle, qrDotRadius
    );
    if (!composed) return;
    const img = new Image();
    img.onload = () => { setQrImg(img); setPreview(null); };
    img.src = composed.toDataURL();
  }, [qrRawImg, insertedDesign, qrFgColor, qrBgColor, qrBgTransp,
      logoImg, logoPct, logoRound, logoBg,
      gradEnabled, gradType, gradColor1, gradColor2, gradInvert,
      qrCornerRadius, qrDotStyle, qrDotRadius]);

  /* ── editor state ── */
  const [qrPos,      setQrPos]      = useState({ x: 80, y: 80 });
  const [qrPct,      setQrPct]      = useState(26);
  const [isDragging, setIsDragging] = useState(false);
  const [preview,    setPreview]    = useState(null);
  const [status,     setStatus]     = useState("");
  const [isGen,      setIsGen]      = useState(false);

  /* ── zoom / pan state (canvas preview only) ── */
  const [zoom,      setZoom]      = useState(1);
  const [pan,       setPan]       = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const panStart  = useRef({ mx: 0, my: 0, px: 0, py: 0 });
  const zoomRef   = useRef(1);
  const panRef    = useRef({ x: 0, y: 0 });
  useEffect(() => { zoomRef.current = zoom; }, [zoom]);
  useEffect(() => { panRef.current  = pan;  }, [pan]);

  const posterUpRef = useRef();
  const qrUpRef     = useRef();
  const canvasRef   = useRef();
  const stageRef    = useRef();
  const dragOff     = useRef({ ox: 0, oy: 0 });

  const allPosters = [...DEFAULT_POSTERS, ...userPosters];
  const allQrs     = [...DEFAULT_QRS,     ...userQrs];
  const activePoster = allPosters.find(p => p.id === selectedPosterId);
  const activeQr     = allQrs.find(q => q.id === selectedQrId);

  const resetZoom = useCallback(() => {
    setZoom(1); setPan({ x: 0, y: 0 });
    zoomRef.current = 1; panRef.current = { x: 0, y: 0 };
  }, []);

  /* ── load poster when selection changes ── */
  useEffect(() => {
    if (!activePoster) return;
    applySavedPosterDesign(activePoster);
    loadImgFromUrl(activePoster.url)
      .then(img => { setPosterImg(img); setPreview(null); resetZoom(); })
      .catch(() => {});
  }, [selectedPosterId, activePoster?.url]);

  /* ── load QR when gallery selection changes ── */
  useEffect(() => {
    if (qrMode !== "gallery" || !activeQr) return;
    loadImgFromUrl(activeQr.url)
      .then(img => { setQrRawImg(img); setPreview(null); })
      .catch(() => {});
  }, [selectedQrId, activeQr?.url, qrMode]);

  /* ── load QR from URL ── */
  const loadQrFromUrl = async () => {
    const u = qrUrlInput.trim();
    if (!u) { setQrUrlError("Entrez une URL valide."); return; }
    try {
      setQrUrlError("");
      const img = await loadImgFromUrl(u);
      setQrRawImg(img);
      setPreview(null);
      setQrUrlError("✓ Image chargée !");
    } catch {
      setQrUrlError("❌ Impossible de charger cette URL. Vérifiez l'accès CORS.");
    }
  };

  /* ── canvas draw (applies zoom + pan) ── */
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !posterImg) return;
    const ctx = canvas.getContext("2d");
    const W = canvas.width, H = canvas.height;
    ctx.clearRect(0, 0, W, H);

    const z  = zoomRef.current;
    const px = panRef.current.x;
    const py = panRef.current.y;

    ctx.save();
    // translate so zoom is anchored at pan center
    ctx.translate(px, py);
    ctx.scale(z, z);
    ctx.drawImage(posterImg, 0, 0, W, H);

    if (qrImg) {
      const ratio = qrPct / 100;
      const qW = Math.round(W * ratio);
      const qH = Math.round((qrImg.naturalHeight / qrImg.naturalWidth) * qW);
      const qx = Math.round((qrPos.x / 100) * W - qW / 2);
      const qy = Math.round((qrPos.y / 100) * H - qH / 2);
      ctx.save();
      ctx.shadowColor = "rgba(0,0,0,0.35)";
      ctx.shadowBlur  = 14 / z;
      ctx.drawImage(qrImg, qx, qy, qW, qH);
      ctx.restore();
      if (isDragging) {
        ctx.save();
        ctx.strokeStyle = T.blue;
        ctx.lineWidth   = Math.max(2, W * 0.003) / z;
        ctx.setLineDash([7, 4]);
        ctx.strokeRect(qx - 2, qy - 2, qW + 4, qH + 4);
        ctx.restore();
      }
    }
    ctx.restore();
  }, [posterImg, qrImg, qrPos, qrPct, isDragging, zoom, pan]);

  useEffect(() => {
    if (!posterImg || !canvasRef.current || !stageRef.current) return;
    const maxW = stageRef.current.clientWidth || 680;
    canvasRef.current.width  = maxW;
    canvasRef.current.height = Math.round((posterImg.naturalHeight / posterImg.naturalWidth) * maxW);
    draw();
  }, [posterImg, canvasFloating]);

  useEffect(() => { draw(); }, [draw, canvasFloating]);

  /* ── drag ── */
  const canvasXY = e => {
    const canvas = canvasRef.current;
    const rect   = canvas.getBoundingClientRect();
    const t      = e.touches?.[0] ?? e;
    // raw canvas pixel
    const rx = (t.clientX - rect.left) * (canvas.width  / rect.width);
    const ry = (t.clientY - rect.top)  * (canvas.height / rect.height);
    // convert to poster logical coords (accounting for zoom+pan)
    const z = zoomRef.current, px = panRef.current.x, py = panRef.current.y;
    return { x: (rx - px) / z, y: (ry - py) / z, rx, ry };
  };

  const isOverQr = (px, py) => {
    if (!qrImg || !canvasRef.current) return false;
    const canvas = canvasRef.current;
    const W = canvas.width, H = canvas.height;
    const qW = Math.round(W * (qrPct / 100));
    const qH = Math.round((qrImg.naturalHeight / qrImg.naturalWidth) * qW);
    const cx = (qrPos.x / 100) * W, cy = (qrPos.y / 100) * H;
    return px >= cx - qW / 2 - 8 && px <= cx + qW / 2 + 8 &&
           py >= cy - qH / 2 - 8 && py <= cy + qH / 2 + 8;
  };

  const onDown = e => {
    if (!posterImg) return;
    if (e.touches?.length === 2) return; // pinch handled separately
    const { x, y, rx, ry } = canvasXY(e);
    if (qrImg && isOverQr(x, y)) {
      // drag QR
      e.preventDefault();
      const W = canvasRef.current.width, H = canvasRef.current.height;
      dragOff.current = { ox: x - (qrPos.x / 100) * W, oy: y - (qrPos.y / 100) * H };
      setIsDragging(true);
    } else if (zoomRef.current > 1) {
      // pan the poster
      e.preventDefault();
      panStart.current = { mx: rx, my: ry, px: panRef.current.x, py: panRef.current.y };
      setIsPanning(true);
    }
  };

  const onMove = useCallback(e => {
    if (!canvasRef.current) return;
    if (isDragging) {
      e.preventDefault();
      const { x, y } = canvasXY(e);
      const W = canvasRef.current.width, H = canvasRef.current.height;
      setQrPos({
        x: Math.min(100, Math.max(0, ((x - dragOff.current.ox) / W) * 100)),
        y: Math.min(100, Math.max(0, ((y - dragOff.current.oy) / H) * 100)),
      });
      setPreview(null);
    } else if (isPanning) {
      e.preventDefault();
      const canvas = canvasRef.current;
      const rect   = canvas.getBoundingClientRect();
      const t      = e.touches?.[0] ?? e;
      const rx = (t.clientX - rect.left) * (canvas.width  / rect.width);
      const ry = (t.clientY - rect.top)  * (canvas.height / rect.height);
      const newPx = panStart.current.px + (rx - panStart.current.mx);
      const newPy = panStart.current.py + (ry - panStart.current.my);
      const clamped = clampPan(newPx, newPy, zoomRef.current, canvas);
      panRef.current = clamped;
      setPan(clamped);
    }
  }, [isDragging, isPanning]);

  const onUp = useCallback(() => { setIsDragging(false); setIsPanning(false); }, []);

  useEffect(() => {
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup",   onUp);
    // touch drag for QR (single finger)
    const canvas = canvasRef.current;
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup",   onUp);
    };
  }, [onMove, onUp]);

  //   useEffect(() => {
  //  applySavedPosterDesign(getPubToken)
  // }, [getPubToken]);
  

const applySavedPosterDesign = async (savedItem) => {
  if (!savedItem?.data) return;

  const data = savedItem.data;

  // 1) Recréer l'image poster depuis la DB
  const posterDbId = `db_${savedItem._id}`;

  let posterUrl = null
if(savedItem?._id){
    posterUrl = `${elakay_API}/graphic-promo/${savedItem._id}/image`;
}
  const dbPoster = {
    id: posterDbId,
    label: data.poster?.label || "Saved Poster",
    url: posterUrl,
    isUserUpload: true,
    fromDatabase: true,
    promoId: savedItem._id,
  };

  // setUserPosters((prev) => {
  //   const exists = prev.some((p) => p.id === posterDbId);

  //   if (exists) {
  //     return prev.map((p) => (p.id === posterDbId ? dbPoster : p));
  //   }

  //   return [dbPoster, ...prev];
  // });

  // setSelectedPosterId_(posterDbId);

  // 2) QR style
  setQrFgColor(data.qrStyle?.foregroundColor ?? "#000000");
  setQrBgColor(data.qrStyle?.backgroundColor ?? "#ffffff");
  setQrBgTransp(data.qrStyle?.backgroundTransparent ?? false);

  setQrCornerRadius(data.qrStyle?.cornerRadius ?? 0);
  setQrDotStyle(data.qrStyle?.dotStyle ?? "square");
  setQrDotRadius(data.qrStyle?.dotRadius ?? 30);

  // 3) QR gradient
  setGradEnabled(data.qrGradient?.enabled ?? false);
  setGradType(data.qrGradient?.type ?? "radial");
  setGradColor1(data.qrGradient?.color1 ?? "#0070cc");
  setGradColor2(data.qrGradient?.color2 ?? "#c9a227");
  setGradInvert(data.qrGradient?.invert ?? false);

  // 4) QR logo style
  setLogoPct(data.qrLogo?.sizePercent ?? 22);
  setLogoRound(data.qrLogo?.borderRadius ?? 8);
  setLogoBg(data.qrLogo?.whiteBackground ?? true);

  // 5) QR position
  setQrPos({
    x: data.qrPosition?.x ?? 80,
    y: data.qrPosition?.y ?? 80,
  });

  setQrPct(data.qrPosition?.sizePercent ?? 26);

  // Restore the exact saved QR Designer reference. The destination is stored
  // alongside the id so a missing or changed reference is visible instead of
  // silently substituting another QR code.
  let qrReferenceError = "";
  if (data.qrDesign?.id) {
    const resolvedQr = resolvePosterQrDesign(data.qrDesign, DEFAULT_QRS || []);
    if (resolvedQr.qr) {
      setQrMode("gallery");
      setSelectedQrId(resolvedQr.qr.id);
    } else {
      qrReferenceError = "⚠ Le QR Designer enregistré n'est plus disponible avec sa destination d'origine.";
    }
  }

  // 6) Canvas view
  setZoom(data.canvasView?.zoom ?? 1);
  setPan(data.canvasView?.pan ?? { x: 0, y: 0 });

  zoomRef.current = data.canvasView?.zoom ?? 1;
  panRef.current = data.canvasView?.pan ?? { x: 0, y: 0 };

  // 7) Status / preview
  setStatus(qrReferenceError || data.status || "");
  setPreview(null);

  // 8) Charger l'image poster dans canvas
  try {
    const img = await loadImgFromUrl(posterUrl);
    // setPosterImg(img);
  } catch (err) {
  }
};

  const onCursorMove = useCallback(e => {
    if (!qrImg || !posterImg || !canvasRef.current) return;
    const { x, y } = canvasXY(e);
    canvasRef.current.style.cursor = isDragging ? "grabbing"
      : isOverQr(x, y) ? "grab"
      : isPanning ? "grabbing"
      : "zoom-in";
  }, [qrImg, posterImg, isDragging, isPanning, qrPos, qrPct, zoom, pan]);

  /* ── zoom helpers ── */
  const clampZoom = z => Math.min(5, Math.max(1, z));

  const clampPan = (px, py, z, canvas) => {
    const W = canvas.width, H = canvas.height;
    const maxX = W * (z - 1);
    const maxY = H * (z - 1);
    return {
      x: Math.min(0, Math.max(-maxX, px)),
      y: Math.min(0, Math.max(-maxY, py)),
    };
  };

  /* wheel zoom — centered on cursor position */
  const onWheel = useCallback(e => {
    if (!posterImg || !canvasRef.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const rect   = canvas.getBoundingClientRect();
    // mouse position in canvas logical pixels
    const mx = (e.clientX - rect.left) * (canvas.width  / rect.width);
    const my = (e.clientY - rect.top)  * (canvas.height / rect.height);

    const oldZ = zoomRef.current;
    const delta = e.deltaY > 0 ? -0.15 : 0.15;
    const newZ  = clampZoom(oldZ + delta * oldZ);

    // adjust pan so the point under cursor stays fixed
    const oldPx = panRef.current.x, oldPy = panRef.current.y;
    const newPx = mx - (mx - oldPx) * (newZ / oldZ);
    const newPy = my - (my - oldPy) * (newZ / oldZ);
    const clamped = clampPan(newPx, newPy, newZ, canvas);

    zoomRef.current = newZ;
    panRef.current  = clamped;
    setZoom(newZ);
    setPan(clamped);
    setPreview(null);
  }, [posterImg]);

  /* pinch-to-zoom (touch) */
  const lastPinch = useRef(null);
  const onTouchZoom = useCallback(e => {
    if (e.touches.length !== 2 || !canvasRef.current) return;
    e.preventDefault();
    const t1 = e.touches[0], t2 = e.touches[1];
    const dist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
    const canvas = canvasRef.current;
    const rect   = canvas.getBoundingClientRect();
    const mx = ((t1.clientX + t2.clientX) / 2 - rect.left) * (canvas.width  / rect.width);
    const my = ((t1.clientY + t2.clientY) / 2 - rect.top)  * (canvas.height / rect.height);

    if (lastPinch.current !== null) {
      const ratio  = dist / lastPinch.current;
      const oldZ   = zoomRef.current;
      const newZ   = clampZoom(oldZ * ratio);
      const oldPx  = panRef.current.x, oldPy = panRef.current.y;
      const newPx  = mx - (mx - oldPx) * (newZ / oldZ);
      const newPy  = my - (my - oldPy) * (newZ / oldZ);
      const clamped = clampPan(newPx, newPy, newZ, canvas);
      zoomRef.current = newZ;
      panRef.current  = clamped;
      setZoom(newZ);
      setPan(clamped);
      setPreview(null);
    }
    lastPinch.current = dist;
  }, []);

  const onTouchZoomEnd = useCallback(() => { lastPinch.current = null; }, []);

  /* attach wheel + touch-zoom to canvas */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.addEventListener("wheel",      onWheel,      { passive: false });
    canvas.addEventListener("touchmove",  onTouchZoom,  { passive: false });
    canvas.addEventListener("touchend",   onTouchZoomEnd);
    return () => {
      canvas.removeEventListener("wheel",      onWheel);
      canvas.removeEventListener("touchmove",  onTouchZoom);
      canvas.removeEventListener("touchend",   onTouchZoomEnd);
    };
  }, [onWheel, onTouchZoom, onTouchZoomEnd, posterImg, canvasFloating]);

  const stepZoom = (dir) => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const W = canvas.width, H = canvas.height;
    const oldZ = zoomRef.current;
    const newZ  = clampZoom(oldZ + dir * 0.3 * oldZ);
    // zoom toward center
    const mx = W / 2, my = H / 2;
    const oldPx = panRef.current.x, oldPy = panRef.current.y;
    const newPx = mx - (mx - oldPx) * (newZ / oldZ);
    const newPy = my - (my - oldPy) * (newZ / oldZ);
    const clamped = clampPan(newPx, newPy, newZ, canvas);
    zoomRef.current = newZ; panRef.current = clamped;
    setZoom(newZ); setPan(clamped);
    setPreview(null);
  };

  /* ── user uploads ── */
  const addUserPoster = async file => {
    if (!file?.type.startsWith("image/")) return;
    const id = "up_" + Date.now();
    const url = URL.createObjectURL(file);
    setUserPosters(p => [...p, { id, label: file.name.replace(/\.[^.]+$/, ""), url, userFile: file }]);
    setSelectedPosterId(id);
  };

  const addUserQr = async file => {
    if (!file?.type.startsWith("image/")) return;
    const id  = "uq_" + Date.now();
    const url = URL.createObjectURL(file);
    const img = await loadImgFromFile(file);
    setUserQrs(q => [...q, { id, label: file.name.replace(/\.[^.]+$/, ""), url, userFile: file }]);
    setSelectedQrId(id);
    setQrRawImg(img);
    setQrMode("gallery");
    setPreview(null);
  };

  /* ── generate full-res ── */
  const generate = async () => {
    if (!posterImg || !qrImg) { setStatus("⚠ Sélectionnez un poster et un QR code."); return; }
    setIsGen(true); setStatus("Rendu haute résolution…");
    try {
      const pI = activePoster?.userFile
        ? await loadImgFromFile(activePoster.userFile)
        : await loadImgFromUrl(activePoster.url);

      // Saved Designer QR codes are already final, verified artwork. Reusing
      // the source image preserves their photograph, colors and destination.
      const hiResQr = insertedDesign ? null : buildQrCanvas(
          qrRawImg, qrFgColor, qrBgColor, qrBgTransp,
          logoImg, logoPct, logoRound, logoBg,
          gradEnabled, gradType, gradColor1, gradColor2, gradInvert,
          qrCornerRadius, qrDotStyle, qrDotRadius
        );
      const qI = hiResQr ? await new Promise(res => {
        const img = new Image(); img.onload = () => res(img); img.src = hiResQr.toDataURL();
      }) : qrImg;

      const canvas = document.createElement("canvas");
      canvas.width  = pI.naturalWidth;
      canvas.height = pI.naturalHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(pI, 0, 0);
      const qW = Math.round(canvas.width  * (qrPct / 100));
      const qH = Math.round((qI.naturalHeight / qI.naturalWidth) * qW);
      const qx = Math.round((qrPos.x / 100) * canvas.width  - qW / 2);
      const qy = Math.round((qrPos.y / 100) * canvas.height - qH / 2);
      ctx.save();
      ctx.shadowColor = "rgba(0,0,0,0.4)"; ctx.shadowBlur = 24;
      ctx.drawImage(qI, qx, qy, qW, qH);
      ctx.restore();
      setPreview(canvas.toDataURL("image/png"));
      setStatus("✓ Image prête !");
    } catch (err) {
      console.error(err);
      setStatus("❌ Erreur lors de la génération.");
    }
    setIsGen(false);
  };

  const download = () => {
    if (!preview) return;
    const a = document.createElement("a");
    a.href = preview; a.download = "elakay-poster.png"; a.click();
  };

  /* ── shared input style ── */
  const inputStyle = {
    width: "100%", height: 36, padding: "0 12px",
    border: `0.5px solid ${T.borderMid}`, borderRadius: 8,
    background: T.white, color: T.text, fontSize: 13,
    outline: "none", fontFamily: "inherit", boxSizing: "border-box",
    transition: "border-color .15s, box-shadow .15s",
  };

  const getImageCombinerData = () => {
  return {
    poster: {
      selectedPosterId,
      label: activePoster?.label || "",
      url: activePoster?.url || "",
      isUserUpload: !!activePoster?.userFile,
    },

    // qrCode: {
    //   mode: qrMode,
    //   selectedQrId,
    //   label: activeQr?.label || "",
    //   url: qrMode === "gallery" ? activeQr?.url || "" : qrUrlInput,
    //   isUserUpload: !!activeQr?.userFile,
    // },

    qrStyle: {
      foregroundColor: qrFgColor,
      backgroundColor: qrBgColor,
      backgroundTransparent: qrBgTransp,

      cornerRadius: qrCornerRadius,
      dotStyle: qrDotStyle,
      dotRadius: qrDotRadius,
    },

    qrGradient: {
      enabled: gradEnabled,
      type: gradType,
      color1: gradColor1,
      color2: gradColor2,
      invert: gradInvert,
    },

    qrLogo: {
      hasLogo: !!logoImg,
      sizePercent: logoPct,
      borderRadius: logoRound,
      whiteBackground: logoBg,
    },

    qrPosition: {
      x: qrPos.x,
      y: qrPos.y,
      sizePercent: qrPct,
    },

    // A QR from the QR Code Designer: which design and the link it opens
    // (the same design can be reused in several posters).
    ...(activeQr?.designed ? { qrDesign: { id: String(activeQr.id).replace(/^qrd_/, ""), link: activeQr.value, name: activeQr.label } } : {}),

    // preview: {
    //   hasPreview: !!preview,
    //   imageBase64: preview || null,
    // },

    canvasView: {
      zoom,
      pan,
    },

    status,

    createdAt: new Date().toISOString(),
  };
};



const saveImageCombinerDesign = async (itemId = "") => {
  if (!token) return;
  
  const data = getImageCombinerData();
  let previewImage = activePoster?.userFile || null;
  if (!previewImage && posterImg) {
    const source = document.createElement("canvas");
    source.width = posterImg.naturalWidth || posterImg.width;
    source.height = posterImg.naturalHeight || posterImg.height;
    source.getContext("2d").drawImage(posterImg, 0, 0, source.width, source.height);
    const blob = await new Promise((resolve) => source.toBlob(resolve, "image/png"));
    if (blob) previewImage = new File([blob], "poster-source.png", { type: "image/png" });
  }

  return addUpdateGraphicPromo(
    token,
    itemId, // promo_id dans la route
    "poster",
    data,
    {
      itemId,
      previewImage
    }
  ).then(res => {
    if (!res) return;

    if (res.error) {
      return;
    }

  });
};

const buildImageUrl = (promoId) => {
  return `${elakay_API}/graphic-promo/${promoId}/image`;
};

/* ─── Scale control (en %) ─────────────────────────────────────────────────── */
function ScaleControl({ value, onChange, label="Size" }) {
  const presets = [25, 50, 75, 100, 125, 150, 200];
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
      background: T.light, borderRadius: 8, padding: "6px 10px",
      border: `0.5px solid ${T.border}`, marginBottom: 8,
    }}>
      <span style={{ fontSize: 11, color: T.muted, fontWeight: 600 }}>{label}</span>

      <button
        onClick={() => onChange(Math.max(10, value - 10))}
        style={{
          width: 26, height: 26, borderRadius: 5, border: `0.5px solid ${T.borderMid}`,
          background: T.white, cursor: "pointer", fontSize: 14, color: T.text,
          fontFamily: "inherit",
        }}
      >−</button>

      <input
        type="range" min={10} max={300} step={5} value={value}
        onChange={e => onChange(+e.target.value)}
        style={{ flex: 1, minWidth: 80, accentColor: T.blue }}
      />

      <button
        onClick={() => onChange(Math.min(300, value + 10))}
        style={{
          width: 26, height: 26, borderRadius: 5, border: `0.5px solid ${T.borderMid}`,
          background: T.white, cursor: "pointer", fontSize: 14, color: T.text,
          fontFamily: "inherit",
        }}
      >+</button>

      <input
        type="number" min={10} max={300} step={1} value={value}
        onChange={e => onChange(Math.min(300, Math.max(10, +e.target.value || 100)))}
        style={{
          width: 56, height: 26, textAlign: "center", borderRadius: 5,
          border: `1px solid ${T.blue}`, color: T.blue, background: T.blueBg,
          fontSize: 12, fontWeight: 700, outline: "none", fontFamily: "inherit",
        }}
      />
      <span style={{ fontSize: 11, color: T.blue, fontWeight: 700 }}>%</span>

      <div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
        {presets.map(p => (
          <button key={p} onClick={() => onChange(p)}
            style={{
              padding: "2px 8px", borderRadius: 4, fontSize: 10, cursor: "pointer",
              border: `0.5px solid ${value === p ? T.blue : T.borderMid}`,
              background: value === p ? T.blueBg : T.white,
              color: value === p ? T.blue : T.muted,
              fontFamily: "inherit", fontWeight: value === p ? 700 : 400,
            }}>{p}%</button>
        ))}
      </div>
    </div>
  );
}

  /* ═════════════════════════════════════════════════════════════════════════
     RENDER
  ═════════════════════════════════════════════════════════════════════════ */
  return (
    <div className="qr-root" style={{ fontFamily: "inherit" }}>

      {/* ── Carte 1 : Poster ── */}
      <Card>
        <CardHeader icon="🖼" iconType="info" title={imageCombinerText("t003", "Choose the poster")} badge={allPosters.length}>
        </CardHeader>
        <CardBody>
          <label className="qr-f-label">{imageCombinerText("t004", "Select a poster or upload your own")}</label>
          <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4, alignItems: "flex-end" }}>
            {allPosters.map(p => (
             
              <div key={p.id} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, flexShrink: 0 }}>
                <Thumb url={p.url} label={p.label} selected={selectedPosterId === p.id}
                  onClick={() => { setSelectedPosterId(p.id); setPreview(null); }} />
                <span style={{ fontSize: 9, color: T.muted, maxWidth: 82, textAlign: "center",
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.label}</span>
              </div>
            ))}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, flexShrink: 0 }}>
              <UploadTile label={imageCombinerText("t024", "Importer")} onFile={addUserPoster} inputRef={posterUpRef} />
              <span style={{ fontSize: 9, color: T.muted }}>{imageCombinerText("t006", "My posters")}</span>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* ── Carte 2 : QR Code source ── */}
      <Card>
        <CardHeader icon="▪" iconType="success" title={imageCombinerText("t007", "Choose the QR code")}>
          {/* Mode toggle */}
          <div style={{ display: "flex", background: T.light, borderRadius: 20, padding: 2, gap: 2 }}>
            {["gallery", "url"].map(m => (
              <button key={m} onClick={() => setQrMode(m)}
                style={{
                  padding: "3px 10px", borderRadius: 18, fontSize: 11, fontWeight: 500,
                  border: "none", cursor: "pointer", transition: "all .15s",
                  background: qrMode === m ? T.white : "transparent",
                  color: qrMode === m ? T.blue : T.muted,
                  boxShadow: qrMode === m ? "0 1px 4px rgba(0,0,0,0.1)" : "none",
                }}>{m === "gallery" ? elakayAutoText("h_1e88ec32fff722", "Galerie") : elakayAutoText("h_0e2d9b0777a485", "URL")}</button>
            ))}
          </div>
        </CardHeader>
        <CardBody>
          {qrMode === "gallery" ? (
            <>
              <label className="qr-f-label">{imageCombinerText("t008", "Select a QR code or upload your own")}</label>
              {(DEFAULT_QRS || []).some(q => q.designed) && (
                <p style={{ margin: "0 0 6px", fontSize: 11, color: T.muted }}>
                  <strong>{elakayAutoText("qrd_insertQr", "Insert QR Code")}</strong> — {elakayAutoText("qrd_insertHint", "vos QR codes du QR Code Designer (marques ★) sont inseres tels quels ; deplacez-les et redimensionnez-les sur le poster.")}
                </p>
              )}
              <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4, alignItems: "flex-end" }}>
                {allQrs.map(q => (
                  <div key={q.id} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, flexShrink: 0 }}>
                    <Thumb url={q.url} label={q.label} selected={selectedQrId === q.id} size={70}
                      onClick={() => { setSelectedQrId(q.id); setPreview(null); }} />
                    {q.designed && <span data-designed-qr={q.id} style={{ fontSize: 9, color: T.blue, fontWeight: 700 }}>★ Designer</span>}
                    <span style={{ fontSize: 9, color: T.muted, maxWidth: 70, textAlign: "center",
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{q.label}</span>
                  </div>
                ))}
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, flexShrink: 0 }}>
                  <UploadTile label={imageCombinerText("t024", "Importer")} onFile={addUserQr} inputRef={qrUpRef} size={70} />
                  <span style={{ fontSize: 9, color: T.muted }}>{imageCombinerText("t010", "My QR codes")}</span>
                </div>
              </div>
            </>
          ) : (
            <>
              <label className="qr-f-label">{__elakayText("t011", "Direct QR code image URL")}</label>
              <div style={{ display: "flex", gap: 8 }}>
                <div className="qr-input-group" style={{ flex: 1 }}>
                  <input
                    type="url"
                    placeholder="https://example.com/qr-code.png"
                    value={qrUrlInput}
                    onChange={e => { setQrUrlInput(e.target.value); setQrUrlError(""); }}
                    onKeyDown={e => e.key === "Enter" && loadQrFromUrl()}
                    style={{ ...inputStyle, paddingRight: 12 }}
                    onFocus={e => { e.target.style.borderColor = T.blue; e.target.style.boxShadow = `0 0 0 2px ${T.blueFocus}`; }}
                    onBlur={e  => { e.target.style.borderColor = T.borderMid; e.target.style.boxShadow = "none"; }}
                  />
                </div>
                <button
                  onClick={loadQrFromUrl}
                  disabled={!qrUrlInput.trim()}
                  className="qr-gen-btn"
                  style={{ width: "auto", padding: "0 16px", flexShrink: 0, height: 36, fontSize: 12 }}
                >{__elakayText("t070", "Load")}</button>
              </div>
              {qrUrlError && (
                <div style={{
                  fontSize: 11, padding: "5px 10px", borderRadius: 6,
                  background: qrUrlError.startsWith("✓") ? T.greenBg : T.redBg,
                  color: qrUrlError.startsWith("✓") ? T.green : T.red,
                  border: `0.5px solid ${qrUrlError.startsWith("✓") ? T.green : T.red}22`,
                }}>{qrUrlError}</div>
              )}
              {/* Also allow generating a QR from a URL string via the QR service */}
              <div style={{ borderTop: `0.5px solid ${T.border}`, paddingTop: 12 }}>
                <label className="qr-f-label">{imageCombinerText("t012", "or generate a QR code from a URL")}</label>
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    id="qr-gen-url"
                    type="url"
                    value={qrUrlInput}
                    placeholder="https://elakay.com/ma-promo"
                    style={{ ...inputStyle, flex: 1 }}
                    onFocus={e => { e.target.style.borderColor = T.blue; e.target.style.boxShadow = `0 0 0 2px ${T.blueFocus}`; }}
                    onBlur={e  => { e.target.style.borderColor = T.borderMid; e.target.style.boxShadow = "none"; }}
                  />
                  <button
                    onClick={() => {
                      const v = document.getElementById("qr-gen-url").value.trim();
                      if (!v) return;
                      const encoded = encodeURIComponent(v);
                      const url = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encoded}&color=000000&bgcolor=ffffff`;
                      setQrUrlInput(url);
                      loadImgFromUrl(url).then(img => {
                        setQrRawImg(img); setPreview(null);
                        setQrUrlError("✓ QR code généré !");
                      }).catch(() => setQrUrlError("❌ Erreur de génération."));
                    }}
                    className="qr-gen-btn"
                    style={{ width: "auto", padding: "0 14px", flexShrink: 0, height: 36, fontSize: 12 }}
                  >{__elakayText("t056", "Generate QR")}</button>
                </div>
              </div>
            </>
          )}
        </CardBody>
      </Card>

      {/* ── Carte 2b : QR couleur & logo ── */}
      <Card>
        <CardHeader icon="🎨" iconType="purple" title={imageCombinerText("t013", "QR code color & logo")} />
        <CardBody>
          {/* Color row */}
          <div style={{ display: "flex", gap: 20, flexWrap: "wrap", alignItems: "flex-start" }}>
            {/* FG color */}
            <div style={{ flex: 1, minWidth: 130 }}>
              <label className="qr-f-label">{imageCombinerText("t014", "Module color (foreground)")}</label>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input type="color" value={qrFgColor}
                  onChange={e => { setQrFgColor(e.target.value); setPreview(null); }}
                  style={{ width: 40, height: 34, border: `0.5px solid ${T.border}`, borderRadius: 7, cursor: "pointer", padding: 2 }} />
                <input type="text" value={qrFgColor} maxLength={7}
                  onChange={e => { if (/^#[0-9a-fA-F]{0,6}$/.test(e.target.value)) setQrFgColor(e.target.value); }}
                  style={{ width: 80, height: 34, padding: "0 8px", borderRadius: 7, border: `0.5px solid ${T.borderMid}`, fontSize: 12, fontFamily: "monospace", outline: "none" }} />
              </div>
              {/* FG quick presets */}
              <div style={{ display: "flex", gap: 5, marginTop: 7, flexWrap: "wrap" }}>
                {["#000000","#0070cc","#1D9E75","#c9a227","#dc3545","#ffffff"].map(c => (
                  <div key={c} onClick={() => setQrFgColor(c)}
                    title={c}
                    style={{
                      width: 22, height: 22, borderRadius: 5, cursor: "pointer",
                      background: c, border: qrFgColor === c ? `2px solid ${T.blue}` : `1px solid ${T.border}`,
                      boxShadow: qrFgColor === c ? `0 0 0 2px ${T.blueBg}` : "none",
                      transition: "all .12s",
                    }} />
                ))}
              </div>
            </div>

            {/* BG color */}
            <div style={{ flex: 1, minWidth: 130 }}>
              <label className="qr-f-label">{imageCombinerText("t015", "Background color")}</label>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input type="color" value={qrBgColor}
                  disabled={qrBgTransp}
                  onChange={e => { setQrBgColor(e.target.value); setPreview(null); }}
                  style={{ width: 40, height: 34, border: `0.5px solid ${T.border}`, borderRadius: 7, cursor: "pointer", padding: 2, opacity: qrBgTransp ? 0.4 : 1 }} />
                <input type="text" value={qrBgTransp ? "transparent" : qrBgColor} maxLength={7} readOnly={qrBgTransp}
                  onChange={e => { if (/^#[0-9a-fA-F]{0,6}$/.test(e.target.value)) setQrBgColor(e.target.value); }}
                  style={{ width: 90, height: 34, padding: "0 8px", borderRadius: 7, border: `0.5px solid ${T.borderMid}`, fontSize: 12, fontFamily: "monospace", outline: "none", opacity: qrBgTransp ? 0.5 : 1 }} />
              </div>
              {/* BG quick presets */}
              <div style={{ display: "flex", gap: 5, marginTop: 7, flexWrap: "wrap", alignItems: "center" }}>
                {["#ffffff","#000000","#f8f9fa","#0a0a1a","#e8f3ff"].map(c => (
                  <div key={c} onClick={() => { setQrBgColor(c); setQrBgTransp(false); }}
                    title={c}
                    style={{
                      width: 22, height: 22, borderRadius: 5, cursor: "pointer",
                      background: c, border: (!qrBgTransp && qrBgColor === c) ? `2px solid ${T.blue}` : `1px solid ${T.border}`,
                      transition: "all .12s",
                    }} />
                ))}
                {/* Transparent toggle */}
                <button onClick={() => setQrBgTransp(v => !v)}
                  style={{
                    height: 22, padding: "0 8px", borderRadius: 5, fontSize: 10, cursor: "pointer",
                    border: `1px solid ${qrBgTransp ? T.blue : T.border}`,
                    background: qrBgTransp ? T.blueBg : T.light,
                    color: qrBgTransp ? T.blue : T.muted, fontFamily: "inherit",
                  }}>{imageCombinerText("t016", "Transparent")}</button>
              </div>
            </div>

            {/* Live mini-preview */}
            <div style={{ flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
              <label className="qr-f-label" style={{ textAlign: "center" }}>{imageCombinerText("t018", "QR preview")}</label>
              <div style={{
                width: 80, height: 80, borderRadius: 10, overflow: "hidden",
                border: `0.5px solid ${T.border}`,
                background: qrBgTransp ? "repeating-conic-gradient(#ccc 0% 25%, #fff 0% 50%) 0 0 / 10px 10px" : qrBgColor,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                {qrImg
                  ? <img src={qrImg.src} alt={imageCombinerText("t018", "QR preview")}
                      style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                  : <span style={{ fontSize: 10, color: T.faint }}>{imageCombinerText("t019", "No QR")}</span>
                }
              </div>
            </div>
          </div>

          {/* Logo section */}
          <div style={{ borderTop: `0.5px solid ${T.border}`, paddingTop: 14 }}>
            <label className="qr-f-label">{imageCombinerText("t020", "Logo in the center of the QR code")}</label>
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "flex-start" }}>

              {/* Logo upload zone */}
              <div style={{ flexShrink: 0 }}>
                <input ref={logoUpRef} type="file" accept="image/*" style={{ display: "none" }}
                  onChange={async e => {
                    const file = e.target.files[0];
                    if (!file) return;
                    const img = await loadImgFromFile(file);
                    setLogoImg(img); setPreview(null);
                  }} />
                <div onClick={() => logoUpRef.current.click()}
                  onDrop={async e => {
                    e.preventDefault();
                    const file = e.dataTransfer.files[0];
                    if (!file?.type.startsWith("image/")) return;
                    const img = await loadImgFromFile(file);
                    setLogoImg(img); setPreview(null);
                  }}
                  onDragOver={e => e.preventDefault()}
                  style={{
                    width: 80, height: 80, borderRadius: 10, cursor: "pointer",
                    border: logoImg ? `2px solid ${T.blue}` : `2px dashed ${T.borderMid}`,
                    background: T.light, overflow: "hidden",
                    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                    position: "relative",
                  }}>
                  {logoImg
                    ? <>
                        <img src={logoImg.src} alt={imageCombinerText("t023", "logo")}
                          style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                        <div style={{ position: "absolute", bottom: 3, right: 3, fontSize: 9,
                          background: "#000a", color: "#fff", padding: "1px 4px", borderRadius: 3 }}>{__elakayText("t022", "Change")}</div>
                      </>
                    : <>
                        <span style={{ fontSize: 24, color: T.faint }}>+</span>
                        <span style={{ fontSize: 9, color: T.muted, textAlign: "center" }}>{imageCombinerText("t023", "Logo")}<br/>{imageCombinerText("t024", "Importer")}</span>
                      </>
                  }
                </div>
                {logoImg && (
                  <button onClick={() => { setLogoImg(null); setPreview(null); }}
                    style={{
                      marginTop: 5, width: "100%", padding: "3px 0", borderRadius: 6, fontSize: 10,
                      border: `0.5px solid ${T.red}`, color: T.red, background: "transparent",
                      cursor: "pointer", fontFamily: "inherit",
                    }}>{__elakayText("t025", "Remove")}</button>
                )}
              </div>

              {/* Logo options */}
              {logoImg && (
                <div style={{ flex: 1, minWidth: 180, display: "flex", flexDirection: "column", gap: 10 }}>
                  {/* Logo size */}
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <label className="qr-f-label" style={{ margin: 0 }}>{imageCombinerText("t026", "Logo size")}</label>
                      <span style={{ fontSize: 11, color: T.blue, fontWeight: 700 }}>{logoPct}%</span>
                    </div>
                    <input type="range" min={10} max={45} step={1} value={logoPct}
                      onChange={e => { setLogoPct(+e.target.value); setPreview(null); }}
                      style={{ width: "100%", accentColor: T.blue }} />
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ fontSize: 9, color: T.faint }}>10%</span>
                      <span style={{ fontSize: 9, color: T.faint }}>45%</span>
                    </div>
                  </div>
                  {/* Logo border-radius */}
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <label className="qr-f-label" style={{ margin: 0 }}>{imageCombinerText("t027", "Logo corner radius")}</label>
                      <span style={{ fontSize: 11, color: T.blue, fontWeight: 700 }}>{logoRound}{__elakayText("t071", "px")}</span>
                    </div>
                    <input type="range" min={0} max={50} step={1} value={logoRound}
                      onChange={e => { setLogoRound(+e.target.value); setPreview(null); }}
                      style={{ width: "100%", accentColor: T.blue }} />
                  </div>
                  {/* Logo bg pad toggle */}
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <button onClick={() => { setLogoBg(v => !v); setPreview(null); }}
                      style={{
                        padding: "4px 12px", borderRadius: 20, fontSize: 11, cursor: "pointer",
                        border: `0.5px solid ${logoBg ? T.blue : T.borderMid}`,
                        background: logoBg ? T.blueBg : T.light,
                        color: logoBg ? T.blue : T.muted, fontFamily: "inherit",
                      }}>{logoBg ? elakayAutoText("h_407462b5fd5a62", "✓ Fond blanc") : elakayAutoText("h_90203f2c09f00f", "Sans fond")}</button>
                    <span style={{ fontSize: 10, color: T.faint }}>{imageCombinerText("t028", "Padding around the logo")}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* ── Border-radius section ── */}
          <div style={{ borderTop: `0.5px solid ${T.border}`, paddingTop: 14 }}>
            <label className="qr-f-label">{imageCombinerText("t029", "QR module & corner shape")}</label>
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>

              {/* Module style */}
              <div style={{ flex: 1, minWidth: 180 }}>
                <label className="qr-f-label">{imageCombinerText("t030", "Module style")}</label>
                <div style={{ display: "flex", gap: 6 }}>
                  {[
                    { id: "square",  emoji: "⬛", label: __elakayText("t032", "Square") },
                    { id: "rounded", emoji: "▣",  label: __elakayText("t072", "Rounded") },
                    { id: "dot",     emoji: "●",  label: __elakayText("t073", "Round") },
                  ].map(s => (
                    <button key={s.id} onClick={() => setQrDotStyle(s.id)}
                      style={{
                        flex: 1, padding: "8px 4px", borderRadius: 8, cursor: "pointer",
                        border: `1.5px solid ${qrDotStyle === s.id ? T.blue : T.border}`,
                        background: qrDotStyle === s.id ? T.blueBg : T.light,
                        color: qrDotStyle === s.id ? T.blue : T.muted,
                        fontSize: 11, fontFamily: "inherit", transition: "all .13s",
                        display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
                      }}>
                      <span style={{ fontSize: 18 }}>{s.emoji}</span>
                      <span>{s.label}</span>
                    </button>
                  ))}
                </div>

                {/* Module radius slider (only for rounded/dot) */}
                {qrDotStyle === "rounded" && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <label className="qr-f-label" style={{ margin: 0 }}>{imageCombinerText("t031", "Module radius")}</label>
                      <span style={{ fontSize: 11, color: T.blue, fontWeight: 700 }}>{qrDotRadius}%</span>
                    </div>
                    <input type="range" min={0} max={50} step={1} value={qrDotRadius}
                      onChange={e => setQrDotRadius(+e.target.value)}
                      style={{ width: "100%", accentColor: T.blue }} />
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ fontSize: 9, color: T.faint }}>{imageCombinerText("t032", "Square")}</span>
                      <span style={{ fontSize: 9, color: T.faint }}>{imageCombinerText("t033", "Very rounded")}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Outer corner radius */}
              <div style={{ flex: 1, minWidth: 180 }}>
                <label className="qr-f-label">{imageCombinerText("t034", "Entire QR code corners")}</label>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ fontSize: 11, color: T.muted }}>{__elakayText("t035", "Global border radius")}</span>
                  <span style={{ fontSize: 11, color: T.blue, fontWeight: 700 }}>{qrCornerRadius}%</span>
                </div>
                <input type="range" min={0} max={50} step={1} value={qrCornerRadius}
                  onChange={e => setQrCornerRadius(+e.target.value)}
                  style={{ width: "100%", accentColor: T.blue }} />
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                  <span style={{ fontSize: 9, color: T.faint }}>{imageCombinerText("t036", "None (square)")}</span>
                  <span style={{ fontSize: 9, color: T.faint }}>{__elakayText("t037", "Circle")}</span>
                </div>
                {/* Quick presets */}
                <div style={{ display: "flex", gap: 5 }}>
                  {[
                    { label: "⬛ 0", val: 0 },
                    { label: "▢ 8", val: 8 },
                    { label: "◻ 20", val: 20 },
                    { label: "⬤ 50", val: 50 },
                  ].map(p => (
                    <button key={p.val} onClick={() => setQrCornerRadius(p.val)}
                      style={{
                        flex: 1, padding: "4px 2px", borderRadius: 6, fontSize: 10, cursor: "pointer",
                        border: `0.5px solid ${qrCornerRadius === p.val ? T.blue : T.border}`,
                        background: qrCornerRadius === p.val ? T.blueBg : T.light,
                        color: qrCornerRadius === p.val ? T.blue : T.muted,
                        fontFamily: "inherit", transition: "all .12s",
                      }}>{p.label}</button>
                  ))}
                </div>

                {/* Visual corner preview */}
                <div style={{ marginTop: 10, display: "flex", justifyContent: "center" }}>
                  <div style={{
                    width: 56, height: 56,
                    background: `linear-gradient(135deg, ${qrFgColor || "#000"} 50%, ${qrBgColor || "#fff"} 50%)`,
                    borderRadius: `${qrCornerRadius}%`,
                    border: `1px solid ${T.border}`,
                    transition: "border-radius .2s",
                  }} />
                </div>
              </div>
            </div>
          </div>

          {/* Gradient section */}
          <div style={{ borderTop: `0.5px solid ${T.border}`, paddingTop: 14 }}>
            {/* Toggle */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <label className="qr-f-label" style={{ margin: 0 }}>{imageCombinerText("t038", "Gradient on modules")}</label>
              <button onClick={() => setGradEnabled(v => !v)}
                style={{
                  padding: "4px 14px", borderRadius: 20, fontSize: 11, cursor: "pointer",
                  border: `0.5px solid ${gradEnabled ? T.blue : T.borderMid}`,
                  background: gradEnabled ? T.blue : T.light,
                  color: gradEnabled ? "#fff" : T.muted, fontFamily: "inherit",
                  transition: "all .15s",
                }}>
                {gradEnabled ? elakayAutoText("h_443e75ebdc3c88", "✓ Activé") : elakayAutoText("h_2d32fa951d2dc3", "Désactivé")}
              </button>
            </div>

            {gradEnabled && (
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

                {/* Type selector */}
                <div>
                  <label className="qr-f-label">{imageCombinerText("t039", "Gradient type")}</label>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {[
                      { id: "radial",    label: __elakayText("t057", "⊙ Radial"),       desc: "centre → bord" },
                      { id: "linear-h",  label: __elakayText("t058", "→ Horizontal"),   desc: "gauche → droite" },
                      { id: "linear-v",  label: __elakayText("t059", "↓ Vertical"),     desc: "haut → bas" },
                      { id: "linear-d",  label: __elakayText("t060", "↗ Diagonal"),     desc: "coin → coin" },
                    ].map(opt => (
                      <button key={opt.id} onClick={() => setGradType(opt.id)}
                        title={opt.desc}
                        style={{
                          padding: "5px 12px", borderRadius: 8, fontSize: 11, cursor: "pointer",
                          border: `0.5px solid ${gradType === opt.id ? T.blue : T.borderMid}`,
                          background: gradType === opt.id ? T.blueBg : T.light,
                          color: gradType === opt.id ? T.blue : T.muted,
                          fontFamily: "inherit", transition: "all .12s",
                        }}>{opt.label}</button>
                    ))}
                  </div>
                </div>

                {/* Color 1 & 2 */}
                <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                  {[
                    { label: gradType === "radial" ? "Couleur Centre" : "Couleur Début",
                      val: gradColor1, set: setGradColor1 },
                    { label: gradType === "radial" ? "Couleur Bord"   : "Couleur Fin",
                      val: gradColor2, set: setGradColor2 },
                  ].map(({ label, val, set }) => (
                    <div key={label} style={{ flex: 1, minWidth: 130 }}>
                      <label className="qr-f-label">{label}</label>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <input type="color" value={val}
                          onChange={e => set(e.target.value)}
                          style={{ width: 40, height: 34, border: `0.5px solid ${T.border}`, borderRadius: 7, cursor: "pointer", padding: 2 }} />
                        <input type="text" value={val} maxLength={7}
                          onChange={e => { if (/^#[0-9a-fA-F]{0,6}$/.test(e.target.value)) set(e.target.value); }}
                          style={{ width: 76, height: 34, padding: "0 8px", borderRadius: 7, border: `0.5px solid ${T.borderMid}`, fontSize: 12, fontFamily: "monospace", outline: "none" }} />
                      </div>
                      {/* Presets */}
                      <div style={{ display: "flex", gap: 4, marginTop: 6, flexWrap: "wrap" }}>
                        {["#000000","#0070cc","#1D9E75","#c9a227","#dc3545","#6d28d9","#ffffff","#ff6b6b"].map(c => (
                          <div key={c} onClick={() => set(c)} title={c}
                            style={{
                              width: 20, height: 20, borderRadius: 4, cursor: "pointer",
                              background: c, border: val === c ? `2px solid ${T.blue}` : `1px solid ${T.border}`,
                              transition: "all .1s",
                            }} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Gradient preview bar */}
                <div>
                  <label className="qr-f-label">{imageCombinerText("t040", "Gradient preview")}</label>
                  <div style={{
                    height: 28, borderRadius: 8, border: `0.5px solid ${T.border}`,
                    background: gradType === "radial"
                      ? `radial-gradient(circle, ${gradInvert ? gradColor2 : gradColor1}, ${gradInvert ? gradColor1 : gradColor2})`
                      : gradType === "linear-h"
                        ? `linear-gradient(to right, ${gradInvert ? gradColor2 : gradColor1}, ${gradInvert ? gradColor1 : gradColor2})`
                        : gradType === "linear-v"
                          ? `linear-gradient(to bottom, ${gradInvert ? gradColor2 : gradColor1}, ${gradInvert ? gradColor1 : gradColor2})`
                          : `linear-gradient(to bottom right, ${gradInvert ? gradColor2 : gradColor1}, ${gradInvert ? gradColor1 : gradColor2})`,
                  }} />
                </div>

                {/* Invert toggle */}
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <button onClick={() => setGradInvert(v => !v)}
                    style={{
                      padding: "4px 14px", borderRadius: 20, fontSize: 11, cursor: "pointer",
                      border: `0.5px solid ${gradInvert ? T.warn : T.borderMid}`,
                      background: gradInvert ? T.warnBg : T.light,
                      color: gradInvert ? T.warn : T.muted, fontFamily: "inherit",
                    }}>{imageCombinerText("t041", "Invert colors")}</button>
                  <span style={{ fontSize: 10, color: T.faint }}>
                    {gradType === "radial"
                      ? (gradInvert ? elakayAutoText("h_d5c4fc4e2af003", "Bord → Centre") : elakayAutoText("h_52331ac0a55782", "Centre → Bord"))
                      : (gradInvert ? elakayAutoText("h_e1dcaf03ea3b61", "Fin → Début") : elakayAutoText("h_7f5be280697e52", "Début → Fin"))}
                  </span>
                </div>
              </div>
            )}
          </div>
        </CardBody>
      </Card>
      <Card>
        <CardHeader icon="↔" iconType="warn" title={__elakayText("t061", "Positionner & redimensionner le QR code")} />
        <CardBody>

          {/* Size control */}
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <label className="qr-f-label" style={{ margin: 0 }}>{imageCombinerText("t043", "QR code size")}</label>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <input
                  type="number" min={5} max={80} step={1} value={qrPct}
                  onChange={e => { setQrPct(Math.min(80, Math.max(5, +e.target.value || 5))); setPreview(null); }}
                  style={{
                    width: 54, height: 30, textAlign: "center", borderRadius: 7, fontSize: 13,
                    fontWeight: 700, border: `1px solid ${T.blue}`, color: T.blue,
                    background: T.blueBg, outline: "none", fontFamily: "inherit",
                  }}
                />
                <span style={{ fontSize: 11, color: T.muted }}>%</span>
              </div>
            </div>
            <div className="qr-range-wrap">
              <input type="range" min={5} max={80} step={1} value={qrPct}
                onChange={e => { setQrPct(+e.target.value); setPreview(null); }}
                style={{ width: "100%", accentColor: T.blue }} />
              <div className="qr-ticks">
                {[5, 20, 35, 50, 65, 80].map(v => <span key={v}>{v}%</span>)}
              </div>
            </div>
            {/* Preset chips */}
            <div style={{ display: "flex", gap: 5, marginTop: 8, flexWrap: "wrap" }}>
              {QR_PRESETS.map(p => (
                <button key={p.label} onClick={() => { setQrPct(p.pct); setPreview(null); }}
                  style={{
                    padding: "3px 10px", borderRadius: 20, fontSize: 11, cursor: "pointer",
                    border: `0.5px solid ${qrPct === p.pct ? T.blue : T.borderMid}`,
                    background: qrPct === p.pct ? T.blueBg : T.light,
                    color: qrPct === p.pct ? T.blue : T.muted, fontFamily: "inherit",
                    transition: "all .12s",
                  }}>{p.label} {p.pct}%</button>
              ))}
            </div>
          </div>

          {/* X/Y sliders */}
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            {[{ ax: "x", lbl: "← Gauche / Droite →" }, { ax: "y", lbl: "↑ Haut / Bas ↓" }].map(({ ax, lbl }) => (
              <div key={ax} style={{ flex: 1, minWidth: 180 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                  <label className="qr-f-label" style={{ margin: 0 }}>{lbl}</label>
                  <span style={{ fontSize: 11, color: T.blue, fontWeight: 700 }}>{Math.round(qrPos[ax])}%</span>
                </div>
                <input type="range" min={0} max={100} step={0.5} value={qrPos[ax]}
                  onChange={e => { setQrPos(p => ({ ...p, [ax]: +e.target.value })); setPreview(null); }}
                  style={{ width: "100%", accentColor: T.blue }} />
              </div>
            ))}
          </div>

          {/* Quick grid + Canvas */}
          <div style={{ display: "flex", gap: 14, alignItems: "flex-start", flexWrap: "wrap" }}>
            {/* Quick positions */}
            <div style={{ flexShrink: 0 }}>
              <label className="qr-f-label">{__elakayText("t062", "Positions rapides")}</label>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 38px)", gap: 4 }}>
                {QUICK_POS.map(p => (
                  <button key={p.label}
                    onClick={() => { setQrPos({ x: p.x, y: p.y }); setPreview(null); }}
                    style={{
                      height: 32, borderRadius: 7, fontSize: 14, cursor: "pointer",
                      border: `0.5px solid ${T.border}`, background: T.light,
                      color: T.muted, transition: "all .12s", fontFamily: "inherit",
                    }}
                    onMouseOver={e => { e.currentTarget.style.background = T.blueBg; e.currentTarget.style.color = T.blue; e.currentTarget.style.borderColor = T.blue; }}
                    onMouseOut={e  => { e.currentTarget.style.background = T.light;  e.currentTarget.style.color = T.muted; e.currentTarget.style.borderColor = T.border; }}
                  >{p.label}</button>
                ))}
              </div>
                <button
          onClick={() => setCanvasFloating(!canvasFloating)}
          style={{
            // position: "absolute", top: 4, right: 4, zIndex: 5,
            padding: "4px 10px", borderRadius: 6, fontSize: 10,
            border: `0.5px solid ${T.borderMid}`, background: T.white,
            color: T.muted, cursor: "pointer", fontFamily: "inherit",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          }}
          title={imageCombinerText("t047", "Detach canvas")}
        >{!canvasFloating ? elakayAutoText("h_b76aa518eaa2ba", "⇱ Détacher") : elakayAutoText("h_773d3fbe17a905", "⇲ Rejoindre")}</button>
            </div>

            {/* Canvas */}
            <div ref={stageRef} style={{ flex: 1, minWidth: 240, position: "relative" }}>
  {!posterImg ? (
    <div className="qr-empty-qr" style={{ height: 200 }}>{__elakayText("t063", "Sélectionnez un poster pour voir l'aperçu")}</div>
  ) : (
    <DraggableFloatingPanel
      title={imageCombinerText("t046", "Canvas - Editor")}
      defaultPos={{ x: 80, y: 80 }}
      defaultSize={{ w: 600, h: 500 }}
      isFloating={canvasFloating}
      onToggleFloat={() => setCanvasFloating(f => !f)}
      margins={{ top: 12, bottom: 12, left: 12, right: 12 }}
      theme={T}
      restoreLabel={imageCombinerText("t001", "Restore")}
    >
      {/* Toggle float button (when not floating) */}
      {/* {!canvasFloating && (
        <button
          onClick={() => setCanvasFloating(true)}
          style={{
            position: "absolute", top: 4, right: 4, zIndex: 5,
            padding: "4px 10px", borderRadius: 6, fontSize: 10,
            border: `0.5px solid ${T.borderMid}`, background: T.white,
            color: T.muted, cursor: "pointer", fontFamily: "inherit",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          }}
          title="Detach canvas"
        >Detach</button>
      )} */}

      {/* Zoom controls bar */}
      <div style={{
        display: "flex", alignItems: "center", gap: 6, marginBottom: 6,
        background: T.light, borderRadius: 8, padding: "5px 10px",
        border: `0.5px solid ${T.border}`,
      }}>
        <button onClick={() => stepZoom(-1)}
          disabled={zoom <= 1}
          style={{
            width: 28, height: 28, borderRadius: 6, border: `0.5px solid ${T.borderMid}`,
            background: T.white, fontSize: 16, cursor: zoom > 1 ? "pointer" : "not-allowed",
            color: zoom > 1 ? T.text : T.faint, display: "flex", alignItems: "center",
            justifyContent: "center", fontFamily: "inherit", transition: "all .12s",
          }}>−</button>
        <input type="range" min={1} max={5} step={0.05} value={zoom}
          onChange={e => {
            const newZ = +e.target.value;
            if (!canvasRef.current) return;
            const canvas = canvasRef.current;
            const W = canvas.width, H = canvas.height;
            const mx = W / 2, my = H / 2;
            const oldZ = zoomRef.current;
            const oldPx = panRef.current.x, oldPy = panRef.current.y;
            const newPx = mx - (mx - oldPx) * (newZ / oldZ);
            const newPy = my - (my - oldPy) * (newZ / oldZ);
            const clamped = clampPan(newPx, newPy, newZ, canvas);
            zoomRef.current = newZ; panRef.current = clamped;
            setZoom(newZ); setPan(clamped); setPreview(null);
          }}
          style={{ flex: 1, accentColor: T.blue }} />
        <button onClick={() => stepZoom(1)}
          disabled={zoom >= 5}
          style={{
            width: 28, height: 28, borderRadius: 6, border: `0.5px solid ${T.borderMid}`,
            background: T.white, fontSize: 16, cursor: zoom < 5 ? "pointer" : "not-allowed",
            color: zoom < 5 ? T.text : T.faint, display: "flex", alignItems: "center",
            justifyContent: "center", fontFamily: "inherit", transition: "all .12s",
          }}>+</button>
        <span style={{ fontSize: 11, color: T.blue, fontWeight: 700, minWidth: 36, textAlign: "center" }}>
          {Math.round(zoom * 100)}%
        </span>
        {zoom > 1 && (
          <button onClick={resetZoom}
            style={{
              padding: "3px 8px", borderRadius: 6, border: `0.5px solid ${T.borderMid}`,
              background: T.white, fontSize: 10, cursor: "pointer", color: T.muted,
              fontFamily: "inherit", whiteSpace: "nowrap",
            }}>{imageCombinerText("t049", "Reset")}</button>
        )}
      </div>
 {/* ✨ NOUVEAU : Contrôle de taille en % */}
  <ScaleControl
    value={canvasScale}
    onChange={setCanvasScale}
    label={imageCombinerText("t054", "Canvas size")}
  />
      <div style={{ fontSize: 10, color: T.faint, marginBottom: 4, textAlign: "center" }}>
        {zoom > 1
          ? (qrImg ? elakayAutoText("h_ca153a4f7521d3", "Glisser sur fond = déplacer la vue · Glisser sur QR = déplacer le QR") : elakayAutoText("h_c32b7de643e0fc", "Glisser pour déplacer la vue"))
          : elakayAutoText("h_705d18042005d4", "🖱 Scroll ou pinch pour zoomer · Glisser le QR pour le repositionner")
        }
      </div>
  {/* Wrapper avec scale appliqué */}
  <div style={{
    width: `${canvasScale}%`,
    transition: "width .2s ease",
    overflow: "auto",
  }}>
      <canvas ref={canvasRef}
        style={{ display: "block", width: "100%", borderRadius: 8, userSelect: "none",
          border: `0.5px solid ${T.border}` }}
        onMouseDown={onDown}
        onMouseMove={onCursorMove}
        onTouchStart={e => { if (e.touches.length === 1) onDown(e); }}
        onTouchMove={e  => { if (e.touches.length === 1) onMove(e); }}
        onTouchEnd={onUp}
      />
      </div>
    </DraggableFloatingPanel>
  )}
</div>
          </div>
        </CardBody>
      </Card>

      {/* ── Carte 4 : Générer ── */}
      <Card>
        <CardHeader icon="⚡" iconType="info" title={__elakayText("t064", "Générer l'image finale")} />
        <CardBody>
          <button onClick={generate} disabled={isGen || !posterImg || !qrImg}
            className="qr-gen-btn">
            {isGen ? elakayAutoText("h_58436ede551a32", "⏳ Génération en cours…") : elakayAutoText("h_21c46ee92ae13d", "⚡ Générer l'Image")}
          </button>
          {status && (
            <div style={{
              fontSize: 12, padding: "6px 12px", borderRadius: 7, textAlign: "center",
              background: status.includes("✓") ? T.greenBg : status.includes("❌") ? T.redBg : T.blueBg,
              color:      status.includes("✓") ? T.green   : status.includes("❌") ? T.red   : T.blue,
              border: `0.5px solid ${status.includes("✓") ? T.green : status.includes("❌") ? T.red : T.blue}33`,
            }}>{status}</div>
          )}
        </CardBody>
      </Card>

      {/* ── Carte 5 : Download — apparaît avec animation après génération ── */}
      <div style={{
        overflow: "hidden",
        // maxHeight: preview ? 700 : 0,
        opacity:   preview ? 1   : 0,
        transition: "max-height .5s cubic-bezier(.4,0,.2,1), opacity .4s ease",
      }}>
        <Card>
          <CardHeader icon="⬇" iconType="success" title={imageCombinerText("t051", "Preview & Download")} />
              <button
        onClick={() => setPreviewFloating(!previewFloating)}
        style={{
          // position: "absolute", top: 4, right: 4, zIndex: 5,
          padding: "4px 10px", borderRadius: 6, fontSize: 10,
          border: `0.5px solid ${T.borderMid}`, background: T.white,
          color: T.muted, cursor: "pointer", fontFamily: "inherit",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        }}
        title={__elakayText("t065", "Détacher l'aperçu")}
      >{!previewFloating ? elakayAutoText("h_b76aa518eaa2ba", "⇱ Détacher") : elakayAutoText("h_773d3fbe17a905", "⇲ Rejoindre")}</button>
          <CardBody>
            {/* Preview */}
           <DraggableFloatingPanel
  title={imageCombinerText("t052", "Preview - generated image")}
  defaultPos={{ x: 200, y: 150 }}
  defaultSize={{ w: 500, h: 450 }}
  isFloating={previewFloating}
  onToggleFloat={() => setPreviewFloating(f => !f)}
  theme={T}
  restoreLabel={imageCombinerText("t001", "Restore")}
  margins={{ top: 12, bottom: 12, left: 12, right: 12 }}
>
  <div style={{ position: "relative" }}>
    {/* {!previewFloating && (
      <button
        onClick={() => setPreviewFloating(true)}
        style={{
          position: "absolute", top: 4, right: 4, zIndex: 5,
          padding: "4px 10px", borderRadius: 6, fontSize: 10,
          border: `0.5px solid ${T.borderMid}`, background: T.white,
          color: T.muted, cursor: "pointer", fontFamily: "inherit",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        }}
        title="Détacher l'aperçu"
      >Detach</button>
    )} */}
     {/* ✨ NOUVEAU : Contrôle de taille en % */}
  <ScaleControl
    value={canvasScale}
    onChange={setCanvasScale}
    label={imageCombinerText("t054", "Canvas size")}
  />

    <div style={{
      border: `0.5px solid ${T.border}`, borderRadius: 8,
      overflow: "auto", background: T.light,
      display: "flex", justifyContent: "center",
      maxHeight: previewFloating ? "calc(100% - 10px)" : "none",
    }}>
      {preview && (
        <img 
        src={preview} alt={imageCombinerText("t055", "Result")}
          style={{ 
                width: `${previewScale}%`,
            maxWidth: "none",
            display: "block",
            transition: "width .2s ease",
            maxWidth: "100%", display: "block"
           }} />
      )}
    </div>
  </div>
</DraggableFloatingPanel>

            {/* URL bar showing it's ready */}
            <div className="qr-url-bar">{__elakayText("t066", "✓ Image générée — prête au téléchargement")}</div>
<div style={{ display: "flex", gap: 10 }}>

  {/* ➕ ADD */}
  <button
    onClick={() => saveImageCombinerDesign(null)}
    style={{
      flex: 1,
      height: 40,
      border: "none",
      borderRadius: 8,
      background: "#1d9e75", // vert add
      color: "#fff",
      fontSize: 13,
      fontWeight: 600,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      transition: "opacity .15s",
      boxShadow: "0 2px 8px rgba(29,158,117,0.3)",
    }}
    onMouseOver={e => e.currentTarget.style.opacity = "0.88"}
    onMouseOut={e => e.currentTarget.style.opacity = "1"}
  >{__elakayText("t067", "➕ Add Poster")}</button>

  {/* ✏️ UPDATE */}
  <button
    onClick={() => saveImageCombinerDesign(selectedPosterId)}
    disabled={!selectedPosterId}
    style={{
      flex: 1,
      height: 40,
      border: "none",
      borderRadius: 8,
      background: selectedPosterId ? "#007399" : "#ccc",
      color: "#fff",
      fontSize: 13,
      fontWeight: 600,
      cursor: selectedPosterId ? "pointer" : "not-allowed",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      transition: "opacity .15s",
      boxShadow: selectedPosterId
        ? "0 2px 8px rgba(0,115,153,0.3)"
        : "none",
    }}
    onMouseOver={e => {
      if (selectedPromoId) e.currentTarget.style.opacity = "0.88";
    }}
    onMouseOut={e => {
      if (selectedPromoId) e.currentTarget.style.opacity = "1";
    }}
  >{__elakayText("t068", "✏️ Update Poster")}</button>

</div>
            {/* Download button */}
            <button
              onClick={download}
              style={{
                width: "100%", height: 40, border: "none", borderRadius: 8,
                background: T.green, color: "#fff",
                fontSize: 13, fontWeight: 600, cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                fontFamily: "inherit", transition: "opacity .15s",
                boxShadow: "0 2px 8px rgba(29,158,117,0.3)",
              }}
              onMouseOver={e => e.currentTarget.style.opacity = "0.88"}
              onMouseOut={e  => e.currentTarget.style.opacity = "1"}
            >{__elakayText("t069", "⬇ Télécharger l'Image PNG")}</button>
          </CardBody>
        </Card>
      </div>

 {/* <pre>{JSON.stringify({DataImageCombiner: getImageCombinerData()},null, 2)}</pre> */}
    </div>
  );
}
